import { useEffect } from "react";
import { RemoveScroll } from "react-remove-scroll";
import type { SeriesBts } from "../hooks/useBehindTheScenes";
import VideoVerticalPlayer from "@/components/VideoVerticalPlayer/VideoVerticalPlayer";
import { useAppStateStore } from "@/stores/useAppState";

/**
 * A Behind the Scenes video over the series page (SHO-757): the vertical
 * player alone — no like, share or bookmark — closed by its `X`, by Escape,
 * and on its own at the end. An overlay rather than a route, so the page
 * underneath keeps its scroll and state — which is why it locks the page's
 * scroll (`RemoveScroll`, the lock Radix puts under the paywall): a swipe on
 * the video, the gesture a vertical player invites, scrolled the page under
 * it otherwise, and the X landed the visitor somewhere else.
 */
const BtsPlayer: React.FC<{
  bts: SeriesBts & { url: string };
  onEnded: () => void;
  onClose: () => void;
}> = ({ bts, onEnded, onClose }) => {
  const setBtsPlayerOpen = useAppStateStore((s) => s.setBtsPlayerOpen);

  // Hides the Zendesk launcher, which would float over the video.
  useEffect(() => {
    setBtsPlayerOpen(true);
    return () => setBtsPlayerOpen(false);
  }, [setBtsPlayerOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <RemoveScroll>
      <div
        data-test-id="bts-player"
        role="dialog"
        aria-label={bts.displayTitle}
        className="fixed inset-0 z-60 bg-black"
      >
        <div className="mx-auto h-full w-full max-w-md">
          {/* Not `isOnScreen`: that pushes the tracks into the episodes
              player's store, which nothing reads here. */}
          <VideoVerticalPlayer
            url={bts.url}
            onClose={onClose}
            onEndReached={onEnded}
          />
        </div>
      </div>
    </RemoveScroll>
  );
};

export default BtsPlayer;
